type FlagEvaluationState =
  | 'off'
  | 'on_default_true'
  | 'on_default_false'
  | 'targeted_on'
  | 'targeted_off'
  | 'unknown';

// A tenant whose evaluate call failed has no per-flag states, so its cells carry a state
// LaunchDarkly never returns rather than being indistinguishable from a real result.
export type CellState = FlagEvaluationState | 'failed';

export type TenantEvaluation = {
  tenantName: string;
  states: Record<string, FlagEvaluationState>;
  error: string | null;
};

type FlagClause = {
  attribute: string;
  op: string;
  values: unknown[];
  negate: boolean;
};

type FlagRule = {
  description: string | null;
  serveValue: unknown;
  clauses: FlagClause[];
};

export type FlagDetail = {
  key: string;
  name: string;
  on: boolean;
  fallthroughValue: unknown;
  offValue: unknown;
  rules: FlagRule[];
  prerequisiteKeys: string[];
};

export type FlagSimulationResult = {
  key: string;
  value: boolean;
  state: FlagEvaluationState;
};

type StateMeta = {
  label: string;
  colour: string;
  onValue: boolean;
  targeted: boolean;
};

// colour is a Mantine theme token (used with the `bg` prop and Progress/Badge `color`).
export const STATE_META: Record<CellState, StateMeta> = {
  on_default_true: {
    label: 'On · default true',
    colour: 'green.6',
    onValue: true,
    targeted: false,
  },
  on_default_false: {
    label: 'On · default false',
    colour: 'yellow.5',
    onValue: false,
    targeted: false,
  },
  targeted_on: { label: 'Targeted on', colour: 'blue.6', onValue: true, targeted: true },
  targeted_off: { label: 'Targeted off', colour: 'red.6', onValue: false, targeted: true },
  off: { label: 'Off', colour: 'gray.3', onValue: false, targeted: false },
  unknown: { label: 'Non-boolean', colour: 'gray.1', onValue: false, targeted: false },
  failed: { label: 'Evaluation failed', colour: 'grape.5', onValue: false, targeted: false },
};

export const DISTRIBUTION_ORDER: CellState[] = [
  'targeted_on',
  'on_default_true',
  'on_default_false',
  'targeted_off',
  'off',
  'unknown',
  'failed',
];

export type StateCounts = Record<CellState, number>;

export function emptyStateCounts(): StateCounts {
  return {
    off: 0,
    on_default_true: 0,
    on_default_false: 0,
    targeted_on: 0,
    targeted_off: 0,
    unknown: 0,
    failed: 0,
  };
}

export function resolvedOnCount(counts: StateCounts): number {
  return counts.on_default_true + counts.targeted_on;
}

export function targetedCount(counts: StateCounts): number {
  return counts.targeted_on + counts.targeted_off;
}
